<script lang="ts">
  import { authState } from '$lib/auth.svelte'
  import { ensureServerRoles } from '$lib/server-access.svelte'
  import { servers as serversStore } from '$lib/store'
  import {
    getLibraries,
    editLibrary,
    scanAndRefreshLibraries,
    TRANSLATION_SOURCE_LANGUAGES,
    libraryLanguageLabel,
    languageToSelect,
    languageFromSelect,
  } from '$lib/apis/library/api'
  import { trackOperation, operationTitle } from '$lib/operations.svelte'
  import { requestServer } from '$lib/servers.svelte'
  import { formatServerName } from '$lib/utils'
  import { Button } from '$lib/components/ui/button/index'
  import { Input } from '$lib/components/ui/input/index.js'
  import { Label } from '$lib/components/ui/label/index.js'
  import { Badge } from '$lib/components/ui/badge/index.js'
  import * as Popover from '$lib/components/ui/popover/index.js'
  import * as Select from '$lib/components/ui/select/index.js'
  import { goto } from '@mateothegreat/svelte5-router'
  import { tick, untrack } from 'svelte'
  import LibraryIcon from '@lucide/svelte/icons/library'
  import FolderIcon from '@lucide/svelte/icons/folder'
  import LanguagesIcon from '@lucide/svelte/icons/languages'
  import SettingsIcon from '@lucide/svelte/icons/settings-2'
  import CheckIcon from '@lucide/svelte/icons/check'
  import ChevronsUpDownIcon from '@lucide/svelte/icons/chevrons-up-down'
  import ChevronRightIcon from '@lucide/svelte/icons/chevron-right'
  import ArrowUpIcon from '@lucide/svelte/icons/arrow-up'
  import AlertCircleIcon from '@lucide/svelte/icons/alert-circle'

  let { route }: { route?: any } = $props()

  const serverUid = $derived(
    String(route?.result?.path?.params?.mediaid ?? ''),
  )
  const libraryId = $derived(
    String(route?.result?.path?.params?.libraryid ?? ''),
  )

  type LibraryRow = {
    id: string
    name: string
    kind: string
    path: string
    language?: string
    libraryConfig?: {
      Agent?: string
      Scanner?: string
      agent?: string
      scanner?: string
    } | null
  }

  let loading = $state(true)
  let loadError = $state<string | null>(null)
  let library = $state<LibraryRow | null>(null)

  let pathDraft = $state('')
  let savingPath = $state(false)
  let pathError = $state<string | null>(null)
  let pathSaved = $state(false)

  let languageDraft = $state('')
  let savingLanguage = $state(false)
  let languageError = $state<string | null>(null)
  let languageSaved = $state(false)

  let browseDir = $state('/')
  let folderNames = $state<string[]>([])
  let pathFilter = $state('')
  let pathLoading = $state(false)
  let pathLoadError = $state<string | null>(null)
  let pathPickerOpen = $state(false)
  let pathTriggerRef = $state<HTMLButtonElement>(null!)

  const gatewayServer = $derived(
    ($serversStore as Record<string, any>)[serverUid],
  )

  /** Deployment entry with local roles from /api/v1/auth/identity (not gateway). */
  const accessServer = $derived(
    (authState.servers ?? []).find((s) => s.uid === serverUid),
  )

  /** OWNER / ADMIN on the deployment — gateway only has OWNER vs MEMBER (non-OWNER). */
  const canManage = $derived.by(() => {
    const roles = (accessServer?.roles ?? []).map((r: string) =>
      String(r).toUpperCase(),
    )
    return roles.includes('OWNER') || roles.includes('ADMIN')
  })

  function resolveServer() {
    // Prefer auth entry when present; gateway list still supplies urls/name.
    return accessServer ?? gatewayServer
  }

  const displayName = $derived(library?.name?.trim() || 'Untitled library')

  const agentName = $derived(
    library?.libraryConfig?.Agent ??
      library?.libraryConfig?.agent ??
      '—',
  )
  const scannerName = $derived(
    library?.libraryConfig?.Scanner ??
      library?.libraryConfig?.scanner ??
      '—',
  )

  const kindLabel = $derived(
    String(library?.kind ?? '')
      .toLowerCase()
      .replace(/_/g, ' ') || '—',
  )

  const languageSelectValue = $derived(
    languageToSelect(languageDraft) === 'unknown' ? 'en' : languageToSelect(languageDraft),
  )
  const languageLabel = $derived(libraryLanguageLabel(languageSelectValue))

  function normalizeBrowseDir(p: string): string {
    const collapsed = p.replace(/\/{2,}/g, '/')
    if (!collapsed || collapsed === '/') return '/'
    return collapsed.endsWith('/') ? collapsed : `${collapsed}/`
  }

  function parentBrowseDir(dir: string): string | null {
    const d = normalizeBrowseDir(dir)
    const core = d.replace(/\/+$/, '')
    if (core === '' || core === '/') return null
    const i = core.lastIndexOf('/')
    if (i <= 0) return '/'
    return `${core.slice(0, i)}/`
  }

  function joinDirName(dir: string, name: string): string {
    const base = normalizeBrowseDir(dir).replace(/\/+$/, '') || '/'
    if (base === '/') return `/${name}/`
    return `${base}/${name}/`
  }

  function browseDirToFormPath(dir: string): string {
    const d = normalizeBrowseDir(dir)
    if (d === '/') return '/'
    return d.replace(/\/+$/, '')
  }

  const filteredFolders = $derived(
    pathFilter.trim() === ''
      ? folderNames
      : folderNames.filter((n) =>
          n.toLowerCase().includes(pathFilter.trim().toLowerCase()),
        ),
  )

  const pathTriggerLabel = $derived(
    pathDraft.trim() !== '' ? pathDraft : 'Choose folder on server…',
  )

  function pathBrowseError(err: unknown): string {
    const status = (err as { response?: { status?: number } })?.response?.status
    if (status === 403) {
      return 'You need admin or owner access to browse folders on this server.'
    }
    return 'Could not read this folder.'
  }

  async function loadFolderListing(dir: string) {
    pathLoading = true
    pathLoadError = null
    try {
      const server = resolveServer()
      const { data } = await requestServer(
        server,
        'post',
        'api/v1/readpath',
        { path: normalizeBrowseDir(dir) },
      )
      const r = data as { subpaths?: string[] } | null
      folderNames = [...(r?.subpaths ?? [])].sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: 'base' }),
      )
    } catch (err) {
      pathLoadError = pathBrowseError(err)
      folderNames = []
    } finally {
      pathLoading = false
    }
  }

  let pathPickerWasOpen = false
  $effect(() => {
    if (!pathPickerOpen) {
      pathPickerWasOpen = false
      return
    }
    if (!pathPickerWasOpen) {
      pathFilter = ''
      if (pathDraft.trim() !== '') {
        const p =
          pathDraft === '/' ? '/' : `${pathDraft.replace(/\/+$/, '')}/`
        browseDir = normalizeBrowseDir(p)
      } else {
        browseDir = '/'
      }
    }
    pathPickerWasOpen = true
    void serverUid
    void loadFolderListing(browseDir)
  })

  function goUp() {
    const p = parentBrowseDir(browseDir)
    if (p != null) browseDir = p
  }

  function enterFolder(name: string) {
    browseDir = joinDirName(browseDir, name)
    pathFilter = ''
  }

  function confirmCurrentFolder() {
    pathDraft = browseDirToFormPath(browseDir)
    pathSaved = false
    pathError = null
    pathPickerOpen = false
    tick().then(() => pathTriggerRef?.focus())
  }

  function pathSegments(): { label: string; dir: string }[] {
    const full = browseDirToFormPath(browseDir)
    if (full === '/') return [{ label: '/', dir: '/' }]
    const parts = full.split('/').filter(Boolean)
    const out: { label: string; dir: string }[] = [{ label: '/', dir: '/' }]
    let acc = ''
    for (const part of parts) {
      acc = `${acc}/${part}`
      out.push({ label: part, dir: normalizeBrowseDir(acc) })
    }
    return out
  }

  function jumpToSegment(dir: string) {
    browseDir = normalizeBrowseDir(dir)
    pathFilter = ''
  }

  async function load() {
    if (!serverUid || !libraryId) return
    loading = true
    loadError = null
    try {
      const server = resolveServer()
      if (!server?.urls?.length) {
        loadError = 'Server not found or unreachable.'
        library = null
        return
      }
      // Deployment roles (OWNER/ADMIN/MEMBER) — not gateway membership.
      await ensureServerRoles(server)
      const data = await getLibraries(server)
      const found = (data?.Libraries ?? []).find(
        (l: LibraryRow) => l.id === libraryId,
      )
      if (!found) {
        loadError = 'Library not found.'
        library = null
        return
      }
      library = found
      pathDraft = found.path ?? ''
      pathSaved = false
      pathError = null
      languageDraft = found.language ?? ''
      languageSaved = false
      languageError = null
    } catch (err) {
      console.error('load library settings failed', err)
      loadError = 'Could not load library settings.'
      library = null
    } finally {
      loading = false
    }
  }

  $effect(() => {
    void serverUid
    void libraryId
    // Don't track authState/serversStore reads inside load — identity refresh
    // patches those and would re-trigger this effect in a loop.
    untrack(() => {
      void load()
    })
  })

  async function savePath() {
    if (!canManage || savingPath || !library) return
    const next = pathDraft.trim()
    if (!next) {
      pathError = 'Path is required.'
      return
    }
    if (next === (library.path ?? '')) {
      pathSaved = false
      pathError = null
      return
    }
    const server = resolveServer()
    if (!server) return

    savingPath = true
    pathError = null
    pathSaved = false
    try {
      const agent =
        library.libraryConfig?.Agent ?? library.libraryConfig?.agent
      const scanner =
        library.libraryConfig?.Scanner ?? library.libraryConfig?.scanner
      await editLibrary(server, libraryId, {
        Name: library.name,
        Kind: library.kind,
        Path: next,
        Language: library.language ?? '',
        ...(agent ? { agentIdentifier: agent } : {}),
        ...(scanner ? { scannerIdentifier: scanner } : {}),
      })
      library = { ...library, path: next }
      pathDraft = next
      pathSaved = true

      try {
        const scanData = await scanAndRefreshLibraries(server)
        trackOperation({
          server,
          operation: scanData?.operation ?? {},
          title: operationTitle(
            scanData?.operation?.type,
            'Scanning & refreshing libraries',
          ),
          description: formatServerName(
            gatewayServer?.name ?? accessServer?.name,
            serverUid,
          ),
        })
      } catch (scanErr) {
        console.error('scan after path edit failed', scanErr)
      }
    } catch (err) {
      console.error('save library path failed', err)
      const msg = err instanceof Error ? err.message : ''
      if (msg === 'forbidden') {
        pathError = 'You need admin or owner access to edit this library path.'
      } else {
        pathError =
          msg || 'Could not save library path.'
      }
    } finally {
      savingPath = false
    }
  }

  async function saveLanguage() {
    if (!canManage || savingLanguage || !library) return
    const next = languageDraft
    if (next === (library.language ?? '')) {
      languageSaved = false
      languageError = null
      return
    }
    const server = resolveServer()
    if (!server) return

    savingLanguage = true
    languageError = null
    languageSaved = false
    try {
      const agent =
        library.libraryConfig?.Agent ?? library.libraryConfig?.agent
      const scanner =
        library.libraryConfig?.Scanner ?? library.libraryConfig?.scanner
      await editLibrary(server, libraryId, {
        Name: library.name,
        Kind: library.kind,
        Path: library.path,
        Language: next,
        ...(agent ? { agentIdentifier: agent } : {}),
        ...(scanner ? { scannerIdentifier: scanner } : {}),
      })
      library = { ...library, language: next }
      languageDraft = next
      languageSaved = true
    } catch (err) {
      console.error('save library language failed', err)
      const msg = err instanceof Error ? err.message : ''
      if (msg === 'forbidden') {
        languageError =
          'You need admin or owner access to edit this library language.'
      } else {
        languageError = msg || 'Could not save library language.'
      }
    } finally {
      savingLanguage = false
    }
  }
</script>

<div class="mx-4 mt-20 mb-10 max-w-xl space-y-4 sm:mx-10 md:mx-auto">
  <div
    class="flex min-w-0 flex-col gap-0.5 rounded-md border border-latte-surface1/60
      bg-latte-mantle/85 px-3 py-1.5 shadow-sm backdrop-blur-sm
      dark:border-macchiato-surface1/60 dark:bg-macchiato-mantle/85"
  >
    <span class="text-[8px] leading-none font-light text-latte-peach dark:text-macchiato-peach">
      library
    </span>
    <h1
      class="truncate text-lg font-semibold leading-tight text-latte-text
        dark:text-macchiato-text sm:text-xl"
    >
      Settings
    </h1>
  </div>

  {#if loading && !library}
    <section
      class="rounded-lg border border-latte-surface1/60 bg-latte-mantle/70 px-5 py-8
        text-sm text-latte-text/60 shadow-sm backdrop-blur-sm
        dark:border-macchiato-surface1/60 dark:bg-macchiato-mantle/70 dark:text-macchiato-text/60"
    >
      Loading library settings…
    </section>
  {:else if loadError}
    <section
      class="rounded-lg border border-latte-surface1/60 bg-latte-mantle/70 px-5 py-6
        shadow-sm backdrop-blur-sm dark:border-macchiato-surface1/60 dark:bg-macchiato-mantle/70"
    >
      <p class="text-sm text-latte-red dark:text-macchiato-red">{loadError}</p>
      <Button
        class="mt-4"
        variant="outline"
        size="sm"
        onclick={() => goto(`/media/${serverUid}`)}
      >
        Back to server
      </Button>
    </section>
  {:else if library}
    <section
      class="overflow-hidden rounded-lg border border-latte-surface1/60 bg-latte-mantle/70
        shadow-sm backdrop-blur-sm dark:border-macchiato-surface1/60 dark:bg-macchiato-mantle/70"
    >
      <div
        class="flex items-center gap-2 border-b border-latte-surface1/50 px-5 py-3.5
          dark:border-macchiato-surface1/50"
      >
        <LibraryIcon
          class="size-4 shrink-0 text-latte-peach dark:text-macchiato-peach"
          strokeWidth={1.75}
        />
        <div class="min-w-0">
          <p
            class="text-[10px] font-light uppercase tracking-wide text-latte-text/55
              dark:text-macchiato-text/55"
          >
            Identity
          </p>
          <p class="truncate text-sm font-semibold text-latte-text dark:text-macchiato-text">
            {displayName}
          </p>
        </div>
      </div>

      <div class="space-y-4 px-5 py-4">
        <div class="flex flex-col gap-1.5">
          <p
            class="text-[10px] font-light uppercase tracking-wide text-latte-text/55
              dark:text-macchiato-text/55"
          >
            Kind
          </p>
          <Badge
            variant="outline"
            class="w-fit rounded-md font-medium capitalize border-latte-sky/40
              bg-latte-sky/10 text-latte-sky dark:border-macchiato-sky/40
              dark:bg-macchiato-sky/10 dark:text-macchiato-sky"
          >
            {kindLabel}
          </Badge>
        </div>

        <div class="flex flex-col gap-2 border-t border-latte-surface1/50 pt-4 dark:border-macchiato-surface1/50">
          <div class="flex items-center gap-2">
            <LanguagesIcon
              class="size-4 shrink-0 text-latte-peach dark:text-macchiato-peach"
              strokeWidth={1.75}
            />
            <Label
              for="library-language"
              class="text-[10px] font-light uppercase tracking-wide text-latte-text/55
                dark:text-macchiato-text/55"
            >
              Source language
            </Label>
          </div>
          <p class="text-xs leading-snug text-latte-text/60 dark:text-macchiato-text/60">
            Language of the books in this library. Used to decide whether pages
            need translation.
          </p>
          {#if canManage}
            <div class="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center">
              <Select.Root
                type="single"
                value={languageSelectValue}
                onValueChange={(v) => {
                  languageDraft = languageFromSelect(v)
                  languageSaved = false
                }}
                disabled={savingLanguage}
              >
                <Select.Trigger
                  id="library-language"
                  class="h-9 w-full sm:w-[14rem]"
                  aria-label="Library source language"
                >
                  {languageLabel}
                </Select.Trigger>
                <Select.Content class="z-62">
                  {#each TRANSLATION_SOURCE_LANGUAGES as opt (opt.value)}
                    <Select.Item value={opt.value} label={opt.label}>
                      {opt.label}
                    </Select.Item>
                  {/each}
                </Select.Content>
              </Select.Root>
              <Button
                variant="outline"
                class="h-9 w-full shrink-0 gap-1.5 sm:w-auto"
                disabled={savingLanguage}
                onclick={() => void saveLanguage()}
              >
                {#if languageSaved && !savingLanguage}
                  <CheckIcon class="size-3.5" strokeWidth={2} />
                  Saved
                {:else}
                  Save
                {/if}
              </Button>
            </div>
            {#if languageError}
              <p class="text-sm text-latte-red dark:text-macchiato-red">{languageError}</p>
            {/if}
          {:else}
            <Badge
              variant="outline"
              class="w-fit rounded-md font-medium border-latte-surface1/60
                bg-latte-base/40 text-latte-text/80 dark:border-macchiato-surface1/60
                dark:bg-macchiato-base/40 dark:text-macchiato-text/80"
            >
              {libraryLanguageLabel(library.language)}
            </Badge>
            <p class="text-xs text-latte-text/50 dark:text-macchiato-text/50">
              Only admins and owners can change the source language.
            </p>
          {/if}
        </div>

        <div
          class="flex items-start gap-3 border-t border-latte-surface1/50 pt-4
            dark:border-macchiato-surface1/50"
        >
          <SettingsIcon
            class="mt-0.5 size-4 shrink-0 text-latte-mauve dark:text-macchiato-mauve"
            strokeWidth={1.75}
          />
          <div class="min-w-0 grid flex-1 gap-3 sm:grid-cols-2">
            <div class="min-w-0 flex flex-col gap-1">
              <p
                class="text-[10px] font-light uppercase tracking-wide text-latte-text/55
                  dark:text-macchiato-text/55"
              >
                Metadata agent
              </p>
              <p class="truncate text-sm text-latte-text dark:text-macchiato-text">
                {agentName}
              </p>
            </div>
            <div class="min-w-0 flex flex-col gap-1">
              <p
                class="text-[10px] font-light uppercase tracking-wide text-latte-text/55
                  dark:text-macchiato-text/55"
              >
                Scanner
              </p>
              <p class="truncate text-sm text-latte-text dark:text-macchiato-text">
                {scannerName}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section
      class="overflow-hidden rounded-lg border border-latte-surface1/60 bg-latte-mantle/70
        shadow-sm backdrop-blur-sm dark:border-macchiato-surface1/60 dark:bg-macchiato-mantle/70"
    >
      <div
        class="flex items-center gap-2 border-b border-latte-surface1/50 px-5 py-3.5
          dark:border-macchiato-surface1/50"
      >
        <FolderIcon
          class="size-4 shrink-0 text-latte-green dark:text-macchiato-green"
          strokeWidth={1.75}
        />
        <div class="min-w-0">
          <p
            class="text-[10px] font-light uppercase tracking-wide text-latte-text/55
              dark:text-macchiato-text/55"
          >
            Location
          </p>
          <p class="text-sm font-semibold text-latte-text dark:text-macchiato-text">
            Library path
          </p>
        </div>
      </div>

      <div class="space-y-4 px-5 py-4">
        <div class="flex flex-col gap-2">
          <Label
            id="library-path-label"
            class="text-[10px] font-light uppercase tracking-wide text-latte-text/55
              dark:text-macchiato-text/55"
          >
            Folder on server
          </Label>
          <p
            class="text-xs leading-snug text-latte-text/60 dark:text-macchiato-text/60"
            id="library-path-hint"
          >
            {#if canManage}
              Browse directories on the server, then save to update where this library
              is scanned from.
            {:else}
              Path of the folder scanned for this library.
            {/if}
          </p>

          {#if canManage}
            <div class="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center">
              <div class="min-w-0 flex-1">
                <Popover.Root bind:open={pathPickerOpen}>
                  <Popover.Trigger bind:ref={pathTriggerRef} class="w-full">
                    {#snippet child({ props })}
                      <Button
                        {...props}
                        variant="outline"
                        class="h-9 w-full justify-between gap-2 px-3 text-left font-normal"
                        role="combobox"
                        aria-expanded={pathPickerOpen}
                        aria-labelledby="library-path-label library-path-hint"
                        disabled={savingPath}
                      >
                        <span class="min-w-0 truncate font-mono text-sm">
                          {pathTriggerLabel}
                        </span>
                        <ChevronsUpDownIcon class="size-4 shrink-0 opacity-50" />
                      </Button>
                    {/snippet}
                  </Popover.Trigger>
                  <Popover.Content
                    class="z-62 flex w-[min(calc(100vw-2rem),22rem)] flex-col gap-2 border-latte-surface1/60
                      bg-latte-mantle p-3 dark:border-macchiato-surface1/60 dark:bg-macchiato-mantle"
                    align="start"
                  >
                    <div
                      class="flex flex-wrap items-center gap-1 border-b border-latte-surface1/60 pb-2
                        dark:border-macchiato-surface1/60"
                    >
                      {#each pathSegments() as seg, i (seg.dir)}
                        {#if i > 0}
                          <span
                            class="text-xs text-latte-text/50 dark:text-macchiato-text/50"
                            aria-hidden="true"
                          >/</span>
                        {/if}
                        <button
                          type="button"
                          class="max-w-[8rem] truncate text-xs font-medium text-latte-green
                            hover:underline dark:text-macchiato-green"
                          onclick={() => jumpToSegment(seg.dir)}
                        >
                          {seg.label}
                        </button>
                      {/each}
                    </div>

                    <div class="flex gap-2">
                      <Input
                        bind:value={pathFilter}
                        placeholder="Filter folders…"
                        class="flex-1"
                        aria-label="Filter folders in this directory"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        class="shrink-0"
                        disabled={parentBrowseDir(browseDir) === null}
                        onclick={goUp}
                        aria-label="Parent folder"
                      >
                        <ArrowUpIcon class="size-4" />
                      </Button>
                    </div>

                    <div
                      class="max-h-56 overflow-y-auto rounded-md border border-latte-surface1/60
                        bg-latte-base/50 text-sm dark:border-macchiato-surface1/60
                        dark:bg-macchiato-base/50"
                      role="listbox"
                      aria-label="Subfolders"
                      aria-busy={pathLoading}
                    >
                      {#if pathLoading}
                        <p class="p-3 text-xs text-latte-text/60 dark:text-macchiato-text/60">
                          Loading…
                        </p>
                      {:else if pathLoadError}
                        <p class="p-3 text-xs text-latte-red dark:text-macchiato-red">
                          {pathLoadError}
                        </p>
                      {:else if filteredFolders.length === 0}
                        <p class="p-3 text-xs text-latte-text/60 dark:text-macchiato-text/60">
                          {folderNames.length === 0
                            ? 'No subfolders here. Use “Use this folder” if this is the library root.'
                            : 'No folders match your filter.'}
                        </p>
                      {:else}
                        {#each filteredFolders as name (name)}
                          <button
                            type="button"
                            class="flex w-full items-center gap-2 px-3 py-2 text-left
                              hover:bg-latte-surface1/40 dark:hover:bg-macchiato-surface1/40"
                            onclick={() => enterFolder(name)}
                          >
                            <FolderIcon
                              class="size-4 shrink-0 text-latte-text/50 dark:text-macchiato-text/50"
                            />
                            <span class="min-w-0 flex-1 truncate">{name}</span>
                            <ChevronRightIcon
                              class="size-4 shrink-0 text-latte-text/50 dark:text-macchiato-text/50"
                            />
                          </button>
                        {/each}
                      {/if}
                    </div>

                    <Button
                      type="button"
                      class="w-full bg-latte-green text-white hover:bg-latte-green/90
                        dark:bg-macchiato-green dark:text-macchiato-base
                        dark:hover:bg-macchiato-green/90"
                      onclick={confirmCurrentFolder}
                    >
                      Use this folder
                    </Button>
                  </Popover.Content>
                </Popover.Root>
              </div>

              <Button
                variant="outline"
                class="h-9 w-full shrink-0 gap-1.5 sm:w-auto"
                disabled={savingPath || !pathDraft.trim()}
                onclick={() => void savePath()}
              >
                {#if pathSaved && !savingPath}
                  <CheckIcon class="size-3.5" strokeWidth={2} />
                  Saved
                {:else}
                  Save
                {/if}
              </Button>
            </div>
          {:else}
            <p
              class="rounded-md border border-latte-surface1/50 bg-latte-base/40 px-3 py-2
                font-mono text-sm break-all text-latte-text dark:border-macchiato-surface1/50
                dark:bg-macchiato-base/40 dark:text-macchiato-text"
            >
              {library.path || '—'}
            </p>
            <p class="text-xs text-latte-text/50 dark:text-macchiato-text/50">
              Only admins and owners can change the library path.
            </p>
          {/if}

          {#if pathLoadError && canManage}
            <div
              class="flex items-start gap-2 rounded-md border border-latte-red/35
                bg-latte-red/8 px-3 py-2 text-xs text-latte-red
                dark:border-macchiato-red/35 dark:bg-macchiato-red/10
                dark:text-macchiato-red"
              role="alert"
            >
              <AlertCircleIcon class="mt-0.5 size-3.5 shrink-0" strokeWidth={1.75} />
              <p class="min-w-0 leading-snug">{pathLoadError}</p>
            </div>
          {/if}

          {#if pathError}
            <p class="text-sm text-latte-red dark:text-macchiato-red">{pathError}</p>
          {/if}
        </div>
      </div>
    </section>
  {/if}
</div>
